import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useLocale } from '../../contexts/LocaleContext';
import { API_BASE } from '../../config';
import { LLMModelSelector } from './LLMModelSelector';
import { Input } from '../ui/input';
import { Button } from '../ui/button';
import { Label } from '../ui/label';
import { Card, CardContent } from '../ui/card';
import { AdminAccessDenied } from './AdminAccessDenied';
import { AdminLoadingState } from './admin-ui';

export const SiteSettings: React.FC = () => {
  const { user, token, masterKey } = useAuth();
  const { t } = useLocale();
  const [defaultLlmModel, setDefaultLlmModel] = useState<string>('');
  const [tokensPerCredit, setTokensPerCredit] = useState<number>(350);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    const fetchSettings = async () => {
      if ((!token && !masterKey) || !user?.admin) {
        return;
      }

      try {
        setLoading(true);
        const url = masterKey
          ? `${API_BASE}/api/admin-settings/site?masterKey=${encodeURIComponent(masterKey)}`
          : `${API_BASE}/api/admin-settings/site`;
        const response = await fetch(url, {
          headers: {
            ...(token && !masterKey ? { Authorization: `Bearer ${token}` } : {}),
          },
        });

        if (response.ok) {
          const data = await response.json();
          setDefaultLlmModel(data.defaultLlmModel || '');
          setTokensPerCredit(data.tokensPerCredit || 350);
        }
      } catch (err) {
        console.error('Error fetching site settings:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchSettings();
  }, [token, masterKey, user]);

  const handleSave = async () => {
    if ((!token && !masterKey)) return;

    setSaving(true);
    setSaved(false);
    try {
      const url = masterKey
        ? `${API_BASE}/api/admin-settings/site?masterKey=${encodeURIComponent(masterKey)}`
        : `${API_BASE}/api/admin-settings/site`;
      const response = await fetch(url, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          ...(token && !masterKey ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({
          defaultLlmModel,
          tokensPerCredit,
        }),
      });

      if (response.ok) {
        setSaved(true);
        setTimeout(() => setSaved(false), 3000);
      }
    } catch (err) {
      console.error('Error saving site settings:', err);
    } finally {
      setSaving(false);
    }
  };

  if (!user || !user.admin) {
    return <AdminAccessDenied />;
  }

  if (loading) {
    return <AdminLoadingState />;
  }

  return (
    <Card className="max-w-[900px]">
      <CardContent className="flex flex-col gap-6 p-6">
        <div className="flex flex-col gap-2">
          <Label>{t('default_llm_model')}</Label>
          <LLMModelSelector
            value={defaultLlmModel}
            onChange={setDefaultLlmModel}
            emptyLabel={t('not_set_use_normal_rules')}
          />
        </div>
        <div className="flex flex-col gap-2">
          <Label htmlFor="tokens-per-credit">{t('tokens_per_credit')}</Label>
          <Input
            id="tokens-per-credit"
            type="number"
            value={tokensPerCredit}
            onChange={(e) => setTokensPerCredit(parseInt(e.target.value, 10) || 350)}
            className="max-w-[200px]"
            min="1"
          />
        </div>
        <div className="flex items-center gap-4">
          <Button onClick={handleSave} disabled={saving}>
            {saving ? t('saving') : t('save')}
          </Button>
          {saved && <span className="text-sm text-muted-foreground">{t('saved')}</span>}
        </div>
      </CardContent>
    </Card>
  );
};
